import {
  buildPagedURL,
  setDestroyed,
  setFetched,
} from 'reduxes/slices/api/actions'

import adminApiRoutes from '../../../routes/admin_api_routes.json'
import liteApiRoutes from '../../../routes/lite_api_routes.json'
import {
  findRecordInApiResponse,
  getRecordsFromApiResponse,
} from '../../util/api_response_utils'
import { handleApiError } from '../../util/parse_server_error'
import { setAppState } from '../app_state/actions'
import {FIRST_LIVE_CHATS_FETCH, FIRST_TOPICS_FETCH} from '../app_state/reducers'
import { setTopicsPaging } from '../paging/actions'
import {setLiveChatsPaging} from 'reduxes/slices/paging/actions'

export const TOPICS_FETCH_SIZE = 20

export function getTopicSortIds(topics) {
  return Object.values(topics || {})
    .reduce((acc, topic) => {
      if (topic.participant_mode === 'group_chat' && topic.sort_order) {
        acc.push(topic.sort_order)
      }
      return acc
    }, [])
    .sort((a, b) => b - a)
}

export function getLiveChatSortIds(topics) {
  return Object.values(topics || {})
    .reduce((acc, topic) => {
      if (topic.participant_mode === 'live_chat' && topic.sort_order) {
        acc.push(topic.sort_order)
      }
      return acc
    }, [])
    .sort((a, b) => b - a)
}

export function fetchTopics(options = {}) {
  return function (axios, dispatch, getState) {
    options = {
      isFirstFetch: false,
      ...options,
    }
    const modelName = 'topics'
    const path = adminApiRoutes.admin_api_v1_topics.path
    const url = buildPagedURL(path, {
      after: options.after,
      before: options.before,
      limit: TOPICS_FETCH_SIZE,
    })
    return axios()
      .get(url)
      .then(
        (result) => {
          const topics = getRecordsFromApiResponse(result, modelName).filter(
            (x) => x.participant_mode === 'group_chat',
          )
          const matches = getTopicSortIds(topics)
          const state = getState()
          const {after, hasMoreAfter, before, hasMoreBefore} =
            handlePaging(state.paging.group_chats, matches, options, dispatch, FIRST_TOPICS_FETCH, setTopicsPaging)
          dispatch(setFetched(modelName, result.data.records))
          return Promise.resolve({
            after,
            hasMoreAfter,
            before,
            hasMoreBefore,
          })
        },
        (result) => {
          handleApiError({
            dispatch,
            result,
            path,
          })
          return Promise.reject(result)
        },
      )
  }
}

export function updateTopic(topicId, data) {
  return function (axios, dispatch) {
    if (!topicId) {
      return Promise.reject()
    }

    const path = adminApiRoutes.admin_api_v1_topic.path
    const url = path.replace(':topic_id', topicId)
    return axios()
      .put(url, data)
      .then(
        (result) => {
          dispatch(setFetched('topics', result.data.records))
          return Promise.resolve(result)
        },
        (result) => {
          handleApiError({
            dispatch,
            result,
            path,
          })
          return Promise.reject(result)
        },
      )
  }
}

export function newTopic(data) {
  return function (axios, dispatch) {
    const path = adminApiRoutes.admin_api_v1_topics.path
    return axios()
      .post(path, data)
      .then(
        (result) => {
          const topic = findRecordInApiResponse(result, 'topics')

          dispatch(setFetched('topics', result.data.records))
          return Promise.resolve(topic.id)
        },
        (result) => {
          handleApiError({
            dispatch,
            result,
            path,
          })
          return Promise.reject(result)
        },
      )
  }
}

export function addParticipant(topic, handle) {
  return function (axios, dispatch) {
    if (!topic?.id) {
      return Promise.reject()
    }

    const path = adminApiRoutes.admin_api_v1_topic_participants.path
    const url = path.replace(':topic_id', topic.id)
    return axios()
      .post(url, { handle })
      .then(
        (result) => {
          dispatch(setFetched('participants', result.data.records))
          return Promise.resolve(result)
        },
        (result) => {
          handleApiError({
            dispatch,
            result,
            path,
          })
          return Promise.reject(result)
        },
      )
  }
}

export function removeParticipant(topicId, participantId) {
  return function (axios, dispatch) {
    if (!topicId) {
      return Promise.reject()
    }

    const path = adminApiRoutes.admin_api_v1_topic_participant.path
    const url = path.replace(':topic_id', topicId).replace(':id', participantId)
    return axios()
      .delete(url)
      .then(
        (result) => {
          result.data.destroyed_participant_ids.forEach((pId) => {
            dispatch(setDestroyed('participants', pId))
          })
        },
        (result) => {
          handleApiError({
            dispatch,
            result,
            path,
          })
          return Promise.reject(result)
        },
      )
  }
}

export function rejectParticipant(topic, addContactRequestId) {
  return function (axios, dispatch) {
    if (!topic?.id) {
      return Promise.reject()
    }

    const path = adminApiRoutes.admin_api_v1_topic_add_contact_request.path
    const url = path
      .replace(':topic_id', topic.id)
      .replace(':id', addContactRequestId)
    return axios()
      .delete(url)
      .then(
        () => {
          dispatch(setDestroyed('add_contact_requests', addContactRequestId))
        },
        (result) => {
          handleApiError({
            dispatch,
            result,
            path,
          })
          return Promise.reject(result)
        },
      )
  }
}

export function fetchFiles(id) {
  return function (axios, dispatch) {
    if (!id) {
      return Promise.reject()
    }
    const path = adminApiRoutes.files_admin_api_v1_topic.path
    const url = path.replace(':topic_id', id)
    return axios()
      .get(url)
      .then(
        (result) => {
          dispatch(setFetched('files', result.data.records))
          return Promise.resolve(result)
        },
        (result) => {
          handleApiError({
            dispatch,
            result,
            path,
          })
          return Promise.reject(result)
        },
      )
  }
}

export function createThreadTopic(parentTimelineId, parentTopicId) {
  return function (axios, dispatch) {
    const data = {
      timeline_id: parentTimelineId,
      topic_id: parentTopicId,
    }
    const path = adminApiRoutes.admin_api_v1_topics.path
    return axios()
      .post(path, data)
      .then(
        (result) => {
          dispatch(setFetched('topics', result.data.records))
          const topic = findRecordInApiResponse(result, 'topics')
          return Promise.resolve(topic.id)
        },
        (result) => {
          handleApiError({
            dispatch,
            result,
            path,
          })
          return Promise.reject(result)
        },
      )
  }
}

export function generateNewShareLink(topicId) {
  return function (axios, dispatch) {
    const data = {
      topic_id: topicId,
    }
    const path = adminApiRoutes.admin_api_v1_topic_generate_new_share_link.path
    return axios()
      .put(path, data)
      .then(
        (result) => {
          dispatch(setFetched('topics', result.data.records))
          const topic = findRecordInApiResponse(result, 'topics')
          return Promise.resolve(topic.id)
        },
        (result) => {
          handleApiError({
            dispatch,
            result,
            path,
          })
          return Promise.reject(result)
        },
      )
  }
}

function handlePaging(existingPaging, matches, options, dispatch, fetchType, pagingFunc) {
  const {
    before: prevBefore,
    after: prevAfter,
    showBefore: prevShowBefore,
    showAfter: prevShowAfter,
  } = existingPaging

  const maxSortOrder = matches[0]
  const minSortOrder = matches[matches.length - 1]


  const after =
    options.isFirstFetch || !prevAfter || maxSortOrder > prevAfter
      ? maxSortOrder
      : undefined
  const before =
    options.isFirstFetch || !prevBefore || minSortOrder < prevBefore
      ? minSortOrder
      : undefined
  const hasMore = matches.length >= TOPICS_FETCH_SIZE
  const noBeforeAndAfter = !options.after && !options.before
  const hasMoreAfter = options.isFirstFetch
    ? false
    : noBeforeAndAfter
      ? false
      : options.after && hasMore
  const hasMoreBefore = options.isFirstFetch
    ? hasMore
    : noBeforeAndAfter
      ? hasMore
      : options.before && hasMore

  if (options.isFirstFetch) {
    dispatch(setAppState(fetchType, false))
  }
  dispatch(
    pagingFunc({
      after,
      before,
      hasMoreAfter,
      hasMoreBefore,
      showAfter:
        !prevShowAfter || after > prevShowAfter ? after : undefined,
      showBefore:
        !prevShowBefore || before < prevShowBefore
          ? before
          : undefined,
    }),
  )
  return {after, before, hasMoreAfter, hasMoreBefore}
}

export function fetchLiveChats(options = {}) {
  return function (axios, dispatch, getState) {
    options = {
      isFirstFetch: false,
      ...options,
    }
    const modelName = 'topics'
    const path = adminApiRoutes.admin_api_v1_live_chats.path
    const url = buildPagedURL(path, {
      after: options.after,
      before: options.before,
      limit: TOPICS_FETCH_SIZE,
    })
    return axios()
      .get(url)
      .then(
        (result) => {
          const topics = getRecordsFromApiResponse(result, modelName).filter(
            (x) => x.participant_mode === 'live_chat',
          )
          const matches = getLiveChatSortIds(topics)
          const state = getState()
          const {after, hasMoreAfter, before, hasMoreBefore} =
            handlePaging(state.paging.live_chats, matches, options, dispatch, FIRST_LIVE_CHATS_FETCH, setLiveChatsPaging)
          dispatch(setFetched(modelName, result.data.records))

          return Promise.resolve({
            after,
            hasMoreAfter,
            before,
            hasMoreBefore,
          })
        },
        (result) => {
          handleApiError({
            dispatch,
            result,
            path,
          })
          return Promise.reject(result)
        },
      )
  }
}

export function fetchLiveChatTopics() {
  return function (axios, dispatch) {
    const path = liteApiRoutes.lite_api_v1_topics.path
    return axios()
      .get(path)
      .then((result) => {
        dispatch(setFetched('topics', result.data.records))
      })
  }
}
